import { normalizeBusinessStorageId, getCurrentBusinessId } from '../../utils/unifiedDataHelpers';
import { purchaseLineEntityId } from '../../utils/purchaseLineEntityId';

const PERSIST_WARN_FIELDS = [
  'purchases',
  'sales',
  'lots',
  'products',
  'suppliers',
  'customers',
  'orders'
];

function buildEntityVerifyMap(businessId) {
  return {
    suppliers: {
      key: 'entitySuppliers',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntitySuppliers(businessId)
        ),
      id: (row) => row.supplierId || row.id
    },
    products: {
      key: 'entityProducts',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityProducts(businessId)
        ),
      id: (row) => row.productId || row.id
    },
    purchases: {
      key: 'entityPurchases',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityPurchases(businessId)
        ),
      id: (row) => purchaseLineEntityId(row)
    },
    orders: {
      key: 'entityOrders',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityOrders(businessId)
        ),
      id: (row) => row.orderId || row.id
    },
    customers: {
      key: 'entityCustomers',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityCustomers(businessId)
        ),
      id: (row) => row.customerId || row.id
    },
    sales: {
      key: 'entitySales',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntitySales(businessId)
        ),
      id: (row) => row.id || row.orderId
    },
    lots: {
      key: 'entityLots',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityLots(businessId)
        ),
      id: (row) => row.id || row.lotId
    },
    invoices: {
      key: 'entityInvoices',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityInvoices(businessId)
        ),
      id: (row) => row.id || row.invoiceId
    },
    expenses: {
      key: 'entityExpenses',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityExpenses(businessId)
        ),
      id: (row) => row.id
    },
    notifications: {
      key: 'entityNotifications',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityNotifications(businessId)
        ),
      id: (row) => row.id || row.notificationId
    },
    expiredProducts: {
      key: 'entityExpiredProducts',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityExpiredProducts(businessId)
        ),
      id: (row) => row.id || row.lotId
    },
    workerSessions: {
      key: 'entityWorkerSessions',
      load: () =>
        import('../../infrastructure/storage/entityAggregateStore').then((m) =>
          m.listEntityWorkerSessions(businessId)
        ),
      id: (row) => row.id || row.sessionId
    },
    discrepancyManagement: {
      key: 'entityDiscrepancyManagement',
      load: async () => {
        const m = await import('../../infrastructure/storage/entityAggregateStore');
        const doc = await m.getEntityDiscrepancyManagement(businessId);
        return doc?.selfDeviations || doc?.handoverDeviations
          ? [...(doc.selfDeviations || []), ...(doc.handoverDeviations || [])]
          : [];
      },
      id: (row) => row.id
    }
  };
}

export function resolveBusinessIdForPersistence(currentBusinessId, businessIdRef) {
  return normalizeBusinessStorageId(
    currentBusinessId ||
      businessIdRef?.current ||
      getCurrentBusinessId({ allowFallback: false }) ||
      getCurrentBusinessId({ allowFallback: false })
  );
}

export function resolveBusinessIdForFieldSave(currentBusinessId, businessIdRef) {
  return normalizeBusinessStorageId(
    currentBusinessId ||
      businessIdRef?.current ||
      getCurrentBusinessId({ allowFallback: false })
  );
}

/**
 * Persist one business field via localStorageManager (+ optional dev verify).
 */
export async function executeBusinessFieldSave(
  businessId,
  field,
  value,
  options = {},
  { onAfterSuccess } = {}
) {
  if (!businessId) {
    if (
      process.env.NODE_ENV === 'development' &&
      PERSIST_WARN_FIELDS.includes(field)
    ) {
      console.warn(
        '[DataContext] saveBusinessData skipped: no businessId (data will not persist after reload)',
        field
      );
    }
    return false;
  }

  const { getPhase1BootstrapOptions } = await import(
    '../../infrastructure/storage/storagePhase1Meta'
  );
  const { initStorageEngine } = await import('../../infrastructure/storage/initStorageEngine');
  await initStorageEngine(getPhase1BootstrapOptions());

  const { localStorageManager } = await import('../../services/localStorageManager');
  const success = await localStorageManager.setBusinessData(field, value, businessId, options);

  if (success && process.env.NODE_ENV === 'development') {
    const devLog = {
      field,
      businessId,
      count: Array.isArray(value) ? value.length : undefined
    };
    const entityVerify = buildEntityVerifyMap(businessId);
    const verify = entityVerify[field];
    if (verify) {
      try {
        const inEntity = await verify.load();
        devLog[verify.key] = inEntity.length;
        devLog.sampleIds = inEntity
          .slice(-3)
          .map(verify.id)
          .filter(Boolean);
      } catch {
        /* optional verify */
      }
    }
    console.info('[DataContext] saveBusinessData OK', devLog);
  }

  if (success) {
    void import('../../services/parallelCloudFieldSync')
      .then(({ enqueueParallelCloudFieldSync }) =>
        enqueueParallelCloudFieldSync(businessId, field, value)
      )
      .catch(() => {});
    if (onAfterSuccess) {
      onAfterSuccess(businessId);
    }
  }

  return success;
}

export async function executeBusinessFieldsBatchSave(businessId, fields) {
  if (!businessId) return false;
  const { persistBusinessFieldsBatch } = await import(
    '../../infrastructure/storage/businessPayloadWrite'
  );
  await persistBusinessFieldsBatch(businessId, fields);
  void import('../../services/parallelCloudFieldSync')
    .then(async ({ enqueueParallelCloudFieldSync, scheduleCloudOutboxDrain }) => {
      for (const [field, value] of Object.entries(fields || {})) {
        await enqueueParallelCloudFieldSync(businessId, field, value, { skipDrain: true });
      }
      scheduleCloudOutboxDrain(50);
    })
    .catch(() => {});
  return true;
}
